import React, { useCallback, useContext, useEffect, useState } from 'react';
import { LandingLayout } from '../../components/layouts';
import { TextField } from '@stax-core/truffle-components';
import {
  FieldWrapper,
  Header,
  LayoutWithHeader,
  OffsetCenteredContent,
  Title,
} from './Authentication.style';
import StaxLogo from '../../assets/img/stax.svg';
import styled from 'styled-components';
import {
  TermsAndConditionsModal,
  useTocHelper,
} from '../../../pages/welcome/TermsAndConitionsModal';
import { Form } from '../../../components/Form';
import { AuthStore, BrandStore, updateAuthUser } from '../../../context';
import { User } from '@stax-core/types-omni';
import { Field, FieldError } from '../../../components';
import { PasswordStrength } from '../../../components/PasswordStrength';
import { NextButton } from '../../components/NextButton';
import { useToaster, useWriteableFieldFormValidation } from '../../../hooks';
import {
  mapFormValuesToPayload,
  mapUserToInitialFormValues,
  validateFormValues,
} from '../../../pages/account/AccountFields.util';
import { AccountFieldsValues } from '../../../pages/account/AccountFields.types';
import { coreapi } from '../../../api';
import { history } from '../../../history';

interface FieldValues extends Pick<User, 'email' | 'password'> {}

const ReadOnlyField = styled(FieldWrapper)`
  div > div {
    background-color: #f2f2f2;
    border: 1px solid #bdc9cc;
  }
`;
const ServiceAgreement = styled.div`
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 12px;
  line-height: 18px;
  align-self: end;
  color: #8d9799;
`;

const LinkButton = styled.span`
  color: #009bf2;
  cursor: pointer;
`;

export const SignUpPage = () => {
  const {
    dispatch,
    state: { auth },
  } = useContext(AuthStore);

  const {
    state: { brand },
  } = useContext(BrandStore);

  const [showToc, setShowToc] = useState(false);
  const { registerAcceptance } = useTocHelper();
  const { toaster, toast } = useToaster();

  const authToken = auth && auth.token;
  const sendWelcomeEmail = brand?.sendWelcomeEmail;
  const acknowledgments =
    (auth && auth.user && auth.user.acknowledgments) || {};

  const acceptTermsAndContinue = useCallback(async () => {
    await registerAcceptance();
    history.push('/welcome');
  }, [registerAcceptance]);

  useEffect(() => {
    if (acknowledgments.setPassword) {
      acceptTermsAndContinue();
    }
  }, [acknowledgments.setPassword, acceptTermsAndContinue]);

  const validate = useWriteableFieldFormValidation(validateFormValues);

  const onSubmit = useCallback(
    async (formValues: AccountFieldsValues) => {
      const payload = mapFormValuesToPayload(formValues);
      try {
        const user = await coreapi.put(authToken!, '/self', {
          ...payload,
          // only set flag if merchant's brand have the flag setWelcomeEmail active in AirTable
          // flag is used to conditionally sent user a welcome email
          send_welcome_email: sendWelcomeEmail,
        });
        dispatch(updateAuthUser(user));
        await acceptTermsAndContinue();
      } catch (error) {
        toaster(
          toast.error(error, 'There was a problem setting your password.'),
        );
      }
    },
    [
      authToken,
      dispatch,
      toaster,
      toast,
      sendWelcomeEmail,
      acceptTermsAndContinue,
    ],
  );

  return (
    <LandingLayout reversed={false}>
      <LayoutWithHeader>
        <Header>
          <img src={StaxLogo} alt="Stax Logo" />
        </Header>
        <Form<FieldValues>
          initialValues={mapUserToInitialFormValues(auth && auth.user)}
          onSubmit={onSubmit}
          validate={validate}
        >
          {formProps => (
            <OffsetCenteredContent
              id="sign-in-page"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 100 }}
              exit={{ opacity: 0, y: -20 }}
              style={{ margin: '0 auto' }}
            >
              <div></div>
              <Title>Set a password for your account</Title>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <p>
                  Create a strong password. This will be how you log in to {brand?.platformName || 'Stax'}.
                </p>
                <ReadOnlyField>
                  <Field label="Email" name="email">
                    {props => <TextField {...props.input} readOnly />}
                  </Field>
                  <FieldError name="email" />
                </ReadOnlyField>

                <FieldWrapper>
                  <Field label="Password" name="password" type="password">
                    {props => <TextField {...props.input} />}
                  </Field>
                  <Field name="password">
                    {fieldProps =>
                      (fieldProps.meta.touched || fieldProps.meta.active) && (
                        <PasswordStrength value={fieldProps.input.value} />
                      )
                    }
                  </Field>
                </FieldWrapper>
                <div>
                  <NextButton
                    id="gogurt-sign-up"
                    text={'Get Started'}
                    formProps={formProps}
                  />
                </div>
              </div>
              <ServiceAgreement>
                By signing up you agree to provide us with information to comply
                with requirements from regulators, financial partners and the
                terms of our{' '}
                <LinkButton onClick={() => setShowToc(true)}>
                  Service Agreement.
                </LinkButton>
              </ServiceAgreement>
            </OffsetCenteredContent>
          )}
        </Form>
      </LayoutWithHeader>
      <TermsAndConditionsModal showToc={showToc} setShowToc={setShowToc} />
    </LandingLayout>
  );
};

export default SignUpPage;
